// TAO Solutions Logo Component - Premium Midnight Luxe Design
// Integrates the company branding with elegant animations and variants
import { cn } from '@/lib/utils';

interface TaoLogoProps {
  variant?: 'full' | 'mark' | 'wordmark';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  colorScheme?: 'gold' | 'white' | 'dark' | 'gradient';
  className?: string;
  animated?: boolean;
}

const sizes = {
  sm: { full: 'h-8', mark: 'h-6', wordmark: 'h-4' },
  md: { full: 'h-12', mark: 'h-10', wordmark: 'h-5' },
  lg: { full: 'h-16', mark: 'h-14', wordmark: 'h-6' },
  xl: { full: 'h-20', mark: 'h-18', wordmark: 'h-8' },
};

export function TaoLogo({
  variant = 'full',
  size = 'md',
  colorScheme = 'gold',
  className,
  animated = true,
}: TaoLogoProps) {
  const colors = {
    gold: 'hsl(var(--gold-400))',
    white: 'hsl(var(--foreground))',
    dark: '#4A4A4B',
    gradient: 'url(#taoGradient)',
  };

  const fillColor = colors[colorScheme];

  if (variant === 'mark') {
    return (
      <svg
        viewBox="0 0 67 70"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(
          sizes[size].mark,
          'w-auto',
          animated && 'transition-all duration-300 hover:drop-shadow-[0_0_12px_hsl(var(--gold-500)/0.5)]',
          className
        )}
      >
        <defs>
          <linearGradient id="taoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--gold-300))" />
            <stop offset="50%" stopColor="hsl(var(--gold-500))" />
            <stop offset="100%" stopColor="hsl(var(--gold-400))" />
          </linearGradient>
        </defs>
        {/* TAO Mark - the distinctive curved shape */}
        <path
          d="M43.6174 42.3754C40.0776 42.3754 39.3213 46.0859 39.3213 49.1191C39.3213 52.1523 39.957 55.8954 43.4968 55.8954C47.0366 55.8954 47.8543 52.0876 47.8543 48.8931C47.8543 45.9245 46.9774 42.3746 43.6181 42.3746L43.6174 42.3754Z"
          fill={fillColor}
        />
        <path
          d="M0 44.6145C0 58.645 8.4563 70 21.6121 70C46.6689 70 66.7143 50.9638 66.7143 23.9019V0.85907C57.6339 0.85907 0 11.5485 0 44.6145ZM15.8413 57.2843C14.268 57.7043 13.451 57.8976 11.9384 57.8976C10.4257 57.8976 9.4874 57.5103 9.06356 56.7683C8.76103 56.2524 8.63972 55.6063 8.63972 53.9931V42.6996H5.97758V40.8596C9.09352 40.8277 9.66862 38.7304 9.66862 34.2773H11.9077V40.8596H15.4482V42.6996H11.9077V54.1544C11.9077 55.3492 12.3322 55.6398 13.5117 55.6398C14.2073 55.6398 14.8431 55.5424 15.8413 55.3164V57.285V57.2843ZM33.9348 57.4128C32.9666 57.7994 32.1803 57.9288 31.3333 57.9288C29.3661 57.9288 28.7011 56.7029 28.5798 54.8948C27.7629 57.0567 25.2819 57.9935 23.2841 57.9935C20.6826 57.9935 18.6555 56.5088 18.6555 53.5395C18.6555 50.5702 20.8031 48.925 23.3454 47.9898C25.0393 47.3773 26.6126 46.9891 28.4286 46.7312V45.312C28.4286 43.0527 26.7646 42.278 25.1 42.278C24.0711 42.278 23.0422 42.5687 22.4978 43.0199C23.1328 43.3745 23.436 43.8913 23.436 44.6012C23.436 45.8271 22.5884 46.5691 21.5595 46.5691C20.1681 46.5691 19.4717 45.6011 19.4717 44.2131C19.4717 41.0186 23.2833 40.2439 25.5231 40.2439C28.1852 40.2439 31.6044 41.1799 31.6044 44.6005V54.4747C31.6044 55.4099 31.7557 55.83 32.542 55.83C32.9965 55.83 33.4803 55.6367 33.9341 55.4107V57.4121L33.9348 57.4128ZM43.4361 57.9288C38.3829 57.9288 35.72 54.0889 35.72 48.9577C35.72 43.8266 38.958 40.2774 43.7379 40.2774C48.5178 40.2774 51.4539 44.1173 51.4539 49.1191C51.4539 54.1209 48.3679 57.9288 43.4361 57.9288Z"
          fill={fillColor}
        />
        <path
          d="M21.9834 52.927C21.9834 54.2822 22.8004 55.3796 24.0712 55.3796C26.4921 55.3796 28.4287 53.5395 28.4287 50.9264V48.699C27.551 48.699 21.9834 49.7324 21.9834 52.9262V52.927Z"
          fill={fillColor}
        />
      </svg>
    );
  }

  // Full logo with "SOLUTIONS" text
  return (
    <svg
      viewBox="0 0 113 70"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        sizes[size].full,
        'w-auto',
        animated && 'transition-all duration-300 hover:drop-shadow-[0_0_16px_hsl(var(--gold-500)/0.4)]',
        className
      )}
    >
      <defs>
        <linearGradient id="taoGradientFull" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(42, 85%, 72%)" />
          <stop offset="50%" stopColor="hsl(38, 72%, 58%)" />
          <stop offset="100%" stopColor="hsl(40, 78%, 65%)" />
        </linearGradient>
      </defs>
      {/* SOLUTIONS text */}
      <path d="M67.7674 48.4052C67.6081 47.7428 67.1024 47.2136 66.2781 47.2136C65.5327 47.2136 65.0899 47.5916 65.0811 48.2727C65.0636 49.7667 68.6808 49.388 68.6808 51.5818C68.6808 52.5747 68.1576 53.5871 66.5265 53.5871C65.1257 53.5871 64.5761 52.6222 64.2393 51.8094L64.8867 51.5639C65.144 52.2824 65.6226 52.9441 66.5616 52.9441C67.5006 52.9441 67.972 52.4149 67.972 51.7151C67.972 49.9756 64.3723 50.4861 64.3723 48.3296C64.3723 47.3461 65.144 46.5714 66.2964 46.5714C67.2895 46.5714 68.0699 47.1286 68.4068 48.2548L67.7681 48.406L67.7674 48.4052Z" fill={fillColor} />
      <path d="M72.3583 52.9441C73.4844 52.9441 74.1494 52.0182 74.1494 50.0792C74.1494 48.1402 73.4844 47.2136 72.3583 47.2136C71.2322 47.2136 70.5672 48.1402 70.5672 50.0792C70.5672 52.0182 71.2322 52.9441 72.3583 52.9441ZM72.3583 46.5714C73.928 46.5714 74.8582 47.8004 74.8582 50.0792C74.8582 52.358 73.9272 53.5871 72.3583 53.5871C70.7894 53.5871 69.8584 52.358 69.8584 50.0792C69.8584 47.8004 70.7894 46.5714 72.3583 46.5714Z" fill={fillColor} />
      <path d="M76.4238 53.4928V46.6657H77.1334V52.8498H80.1653V53.4928H76.4238Z" fill={fillColor} />
      <path d="M82.0043 46.6657V51.4875C82.0043 52.2824 82.5355 52.9433 83.5374 52.9433C84.5392 52.9433 85.072 52.2816 85.072 51.4875V46.6657H85.7808V51.4314C85.7808 52.7929 84.8586 53.5871 83.5374 53.5871C82.2162 53.5871 81.2939 52.7921 81.2939 51.4314V46.6657H82.0035H82.0043Z" fill={fillColor} />
      <path d="M89.7169 47.3087V53.4928H89.0081V47.3087H87.0928V46.6657H91.6322V47.3087H89.7169Z" fill={fillColor} />
      <path d="M93.7283 46.6657H93.0195V53.4928H93.7283V46.6657Z" fill={fillColor} />
      <path d="M97.8671 52.9441C98.9932 52.9441 99.6582 52.0182 99.6582 50.0792C99.6582 48.1402 98.9932 47.2136 97.8671 47.2136C96.741 47.2136 96.076 48.1402 96.076 50.0792C96.076 52.0182 96.741 52.9441 97.8671 52.9441ZM97.8671 46.5714C99.4368 46.5714 100.367 47.8004 100.367 50.0792C100.367 52.358 99.436 53.5871 97.8671 53.5871C96.2982 53.5871 95.3672 52.358 95.3672 50.0792C95.3672 47.8004 96.2982 46.5714 97.8671 46.5714Z" fill={fillColor} />
      <path d="M101.933 53.4928V46.6657H102.952L105.745 51.9232H105.781V46.6657H106.419V53.4928H105.834L102.589 47.403H102.571V53.4928H101.933Z" fill={fillColor} />
      <path d="M111.402 48.4052C111.243 47.7428 110.737 47.2136 109.913 47.2136C109.167 47.2136 108.725 47.5916 108.716 48.2727C108.698 49.7667 112.316 49.388 112.316 51.5818C112.316 52.5747 111.792 53.5871 110.161 53.5871C108.76 53.5871 108.211 52.6222 107.874 51.8094L108.521 51.5639C108.779 52.2824 109.257 52.9441 110.196 52.9441C111.136 52.9441 111.607 52.4149 111.607 51.7151C111.607 49.9756 108.007 50.4861 108.007 48.3296C108.007 47.3461 108.779 46.5714 109.931 46.5714C110.924 46.5714 111.705 47.1286 112.042 48.2548L111.403 48.406L111.402 48.4052Z" fill={fillColor} />
      {/* TAO Mark */}
      <path d="M43.6174 42.3754C40.0776 42.3754 39.3213 46.0859 39.3213 49.1191C39.3213 52.1523 39.957 55.8954 43.4968 55.8954C47.0366 55.8954 47.8543 52.0876 47.8543 48.8931C47.8543 45.9245 46.9774 42.3746 43.6181 42.3746L43.6174 42.3754Z" fill={fillColor} />
      <path d="M0 44.6145C0 58.645 8.4563 70 21.6121 70C46.6689 70 66.7143 50.9638 66.7143 23.9019V0.85907C57.6339 0.85907 0 11.5485 0 44.6145ZM15.8413 57.2843C14.268 57.7043 13.451 57.8976 11.9384 57.8976C10.4257 57.8976 9.4874 57.5103 9.06356 56.7683C8.76103 56.2524 8.63972 55.6063 8.63972 53.9931V42.6996H5.97758V40.8596C9.09352 40.8277 9.66862 38.7304 9.66862 34.2773H11.9077V40.8596H15.4482V42.6996H11.9077V54.1544C11.9077 55.3492 12.3322 55.6398 13.5117 55.6398C14.2073 55.6398 14.8431 55.5424 15.8413 55.3164V57.285V57.2843ZM33.9348 57.4128C32.9666 57.7994 32.1803 57.9288 31.3333 57.9288C29.3661 57.9288 28.7011 56.7029 28.5798 54.8948C27.7629 57.0567 25.2819 57.9935 23.2841 57.9935C20.6826 57.9935 18.6555 56.5088 18.6555 53.5395C18.6555 50.5702 20.8031 48.925 23.3454 47.9898C25.0393 47.3773 26.6126 46.9891 28.4286 46.7312V45.312C28.4286 43.0527 26.7646 42.278 25.1 42.278C24.0711 42.278 23.0422 42.5687 22.4978 43.0199C23.1328 43.3745 23.436 43.8913 23.436 44.6012C23.436 45.8271 22.5884 46.5691 21.5595 46.5691C20.1681 46.5691 19.4717 45.6011 19.4717 44.2131C19.4717 41.0186 23.2833 40.2439 25.5231 40.2439C28.1852 40.2439 31.6044 41.1799 31.6044 44.6005V54.4747C31.6044 55.4099 31.7557 55.83 32.542 55.83C32.9965 55.83 33.4803 55.6367 33.9341 55.4107V57.4121L33.9348 57.4128ZM43.4361 57.9288C38.3829 57.9288 35.72 54.0889 35.72 48.9577C35.72 43.8266 38.958 40.2774 43.7379 40.2774C48.5178 40.2774 51.4539 44.1173 51.4539 49.1191C51.4539 54.1209 48.3679 57.9288 43.4361 57.9288Z" fill={fillColor} />
      <path d="M21.9834 52.927C21.9834 54.2822 22.8004 55.3796 24.0712 55.3796C26.4921 55.3796 28.4287 53.5395 28.4287 50.9264V48.699C27.551 48.699 21.9834 49.7324 21.9834 52.9262V52.927Z" fill={fillColor} />
    </svg>
  );
}

// Compact logo lockup for headers: TAO mark + CreditNova text
export function TaoProductLogo({
  size = 'md',
  className,
}: {
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const textSizes = {
    sm: 'text-lg',
    md: 'text-xl',
    lg: 'text-2xl',
  };

  const markSizes = {
    sm: 'h-7',
    md: 'h-9',
    lg: 'h-11',
  };

  return (
    <div className={cn('flex items-center gap-3 group', className)}>
      {/* TAO Mark with glow effect */}
      <div className="relative flex-shrink-0">
        <TaoLogo
          variant="mark"
          colorScheme="gradient"
          className={cn(markSizes[size], 'transition-all duration-300 group-hover:scale-105')}
        />
        {/* Ambient glow */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl">
          <TaoLogo variant="mark" colorScheme="gold" className={markSizes[size]} />
        </div>
      </div>

      {/* Product name */}
      <div className="flex flex-col">
        <span className={cn('font-display font-semibold text-foreground tracking-tight leading-none', textSizes[size])}>
          Credit<span className="gradient-text">Nova</span>
        </span>
        <span className="text-2xs text-muted-foreground uppercase tracking-widest mt-0.5">
          by TAO Solutions
        </span>
      </div>
    </div>
  );
}

// Footer attribution component
export function PoweredByTao({ className }: { className?: string }) {
  return (
    <a
      href="https://www.taosolutions.ca/"
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group',
        className
      )}
    >
      <span className="text-xs uppercase tracking-wider">Powered by</span>
      <TaoLogo
        variant="full"
        size="sm"
        colorScheme="gold"
        className="h-5 opacity-60 group-hover:opacity-100 transition-opacity"
      />
    </a>
  );
}
